import type { HttpClient } from "../http/client";
import type {
  AttendeeMe,
  AttendeeVerifyResponse,
  Industry,
  InterestTag,
  Lang,
  Profile,
  ProfileInput,
  RegisterRequest,
  RegisterVerifyResponse,
  VerificationResponse,
  VerifyRequest,
} from "../wire/attendee";
import type {
  Brand,
  CancelResponse,
  JoinResponse,
  RsvpListResponse,
  SessionsResponse,
  SubmitResponse,
} from "../wire/event";
import type {
  SurveyLookupRequest,
  SurveyLookupResponse,
  SurveyStateResponse,
  SurveySubmitRequest,
  SurveySubmitResponse,
} from "../wire/survey";
import type { SelfWalkInRequest, SelfWalkInResponse, WalkInStateResponse } from "../wire/walk-in";

/**
 * Everything the attendee app calls (docs/api.md 37, 39, 40, 41, 46, 47). Public
 * routes are sent without a token even when one is held.
 */
export function createAttendeeApi(http: HttpClient) {
  return {
    /* 37 Registration, public */
    register: (body: RegisterRequest) =>
      http.json<VerificationResponse>("POST", "/register", { body, anonymous: true }),
    verifyRegistration: (body: VerifyRequest) =>
      http.json<RegisterVerifyResponse>("POST", "/register/verify", { body, anonymous: true }),
    resendRegistrationCode: (challengeId: string) =>
      http.json<VerificationResponse>("POST", "/register/resend-otp", {
        body: { challenge_id: challengeId },
        anonymous: true,
      }),
    industries: () =>
      http.json<{ industries: Industry[] }>("GET", "/industries", { anonymous: true }),
    interestTags: () =>
      http.json<{ interest_tags: InterestTag[] }>("GET", "/interest-tags", { anonymous: true }),

    /* 39.5, 40.2 The event and its brand, public */
    sessions: () => http.json<SessionsResponse>("GET", "/sessions", { anonymous: true }),
    brand: () => http.json<Brand>("GET", "/brand", { anonymous: true }),

    /* 39.1-39.4a Sign-in and the attendee's own account */
    login: (email: string) =>
      http.json<VerificationResponse>("POST", "/attendee/login", { body: { email }, anonymous: true }),
    verifyLogin: (body: VerifyRequest) =>
      http.json<AttendeeVerifyResponse>("POST", "/attendee/verify", { body, anonymous: true }),
    logout: () => http.void("POST", "/attendee/logout"),
    me: () => http.json<AttendeeMe>("GET", "/attendee/me"),
    saveProfile: (profile: ProfileInput) =>
      http.json<{ profile: Profile }>("PUT", "/attendee/profile", { body: { profile } }),
    setLanguage: (lang: Lang) =>
      http.json<{ language: Lang }>("PUT", "/attendee/language", { body: { lang } }),

    /* 39.6-39.8, 40.1 RSVP, only when event.rsvp_enabled */
    rsvps: () => http.json<RsvpListResponse>("GET", "/rsvps"),
    join: (sessionId: string) =>
      http.json<JoinResponse>("POST", "/rsvps", { body: { session_id: sessionId } }),
    cancel: (sessionId: string) =>
      http.json<CancelResponse>("POST", "/rsvps/cancel", { body: { session_id: sessionId } }),
    submit: () => http.json<SubmitResponse>("POST", "/rsvps/submit"),

    /* 46.1-46.3 The on-the-day survey, public */
    surveyState: () => http.json<SurveyStateResponse>("GET", "/survey", { anonymous: true }),
    surveyLookup: (body: SurveyLookupRequest) =>
      http.json<SurveyLookupResponse>("POST", "/survey/lookup", { body, anonymous: true }),
    surveySubmit: (body: SurveySubmitRequest) =>
      http.json<SurveySubmitResponse>("POST", "/survey/submit", { body, anonymous: true }),

    /* 47 The public walk-in form */
    walkInState: () => http.json<WalkInStateResponse>("GET", "/walk-in", { anonymous: true }),
    walkIn: (body: SelfWalkInRequest) =>
      http.json<SelfWalkInResponse>("POST", "/walk-in", { body, anonymous: true }),
  };
}

export type AttendeeApi = ReturnType<typeof createAttendeeApi>;

/**
 * 40.3: a stored logo or poster. The brand response already carries absolute
 * URLs; this builds one for a poster key the console knows by slug.
 */
export function assetUrl(baseUrl: string, key: string): string {
  return `${baseUrl.replace(/\/+$/, "")}/assets/${encodeURIComponent(key)}`;
}
